import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Crown, ChevronRight, Sparkles, X } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { membership } from '../data/plansData';

const MembershipSection = ({ isAnnual, setIsAnnual }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName] || LucideIcons.Sparkles;
    return Icon;
  };

  const price = isAnnual ? membership.annualPrice : membership.monthlyPrice;

  return (
    <section 
      id="membership"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="membership-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-surface-dark">
        <motion.div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-neon-green/15 blur-[120px]"
          animate={{ 
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-gold/10 blur-[100px]"
          animate={{ 
            x: [0, -30, 0],
            y: [0, -50, 0],
            scale: [1.1, 1, 1.1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span 
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neon-green/40 bg-neon-green/10 text-neon-green text-sm font-semibold mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Crown className="w-4 h-4" />
            Membership Oficial
          </motion.span>
          <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-6xl text-white mb-4">
            Espaços Exclusivos para{' '}
            <span className="gradient-text-green">Verdadeiros Membros</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Colaboração, construção e conexões reais.{' '}
            <span className="text-neon-green font-semibold">Juntos somos mais fortes.</span>
          </p>
        </motion.div>

        {/* Comparison: Free vs Member */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* FREE Plan - Preto e branco, sem graça */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-neutral-900 to-neutral-950 border border-white/5 p-8 grayscale-[0.5] opacity-80 hover:opacity-95 hover:grayscale-0 transition-all duration-500"
            data-testid="free-plan-card"
            whileHover={{ y: -4 }}
          >
            {/* Muted overlay */}
            <div className="absolute inset-0 bg-black/20 pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-neutral-800 border border-neutral-700 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-neutral-400" />
                </div>
                <span className="text-neutral-400 text-sm font-medium uppercase tracking-wider">
                  Espectador
                </span>
              </div>

              <h3 className="font-outfit font-bold text-2xl text-neutral-300 mb-2">
                Comece aqui
              </h3>
              <p className="text-neutral-500 text-sm mb-6">
                Acesso gratuito para explorar o ecossistema
              </p>

              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="font-outfit font-black text-4xl text-neutral-300">
                    Grátis
                  </span>
                </div>
                <p className="text-neutral-500 text-sm mt-1">
                  Acesso básico e limitado
                </p>
              </div>

              {/* Limited features */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-2 text-neutral-500 text-sm">
                  <Check className="w-4 h-4 flex-shrink-0" />
                  <span>Acesso básico ao ecossistema</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-500 text-sm">
                  <Check className="w-4 h-4 flex-shrink-0" />
                  <span>Ver agenda de eventos</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-500 text-sm">
                  <Check className="w-4 h-4 flex-shrink-0" />
                  <span>Mural de recados</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-600 text-sm line-through">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>Espaços exclusivos de membros</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-600 text-sm line-through">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>Grupos temáticos e regionais</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-600 text-sm line-through">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>Reconhecimento e visibilidade</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-600 text-sm line-through">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>Oportunidades de negócios reais</span>
                </div>
              </div>

              {/* Boring CTA */}
              <a
                href={membership.freeCtaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 rounded-lg text-center font-medium bg-neutral-800 text-neutral-400 border border-neutral-700 hover:bg-neutral-700 transition-colors"
                data-testid="free-cta"
              >
                Entrar gratuitamente
              </a>

              <p className="text-neutral-600 text-xs text-center mt-4 italic">
                Se você quer apenas assistir, este é o seu lugar.
              </p>
            </div>
          </motion.div>

          {/* MEMBERSHIP Plan - Vibrante e destacado */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-surface border-2 border-neon-green/50 p-8 shadow-[0_0_40px_rgba(0,255,0,0.15)]"
            data-testid="membership-plan-card"
            whileHover={{ y: -8, boxShadow: '0 0 60px rgba(0, 255, 0, 0.3)' }}
            transition={{ duration: 0.3 }}
          >
            {/* Popular badge */}
            <motion.div 
              className="absolute -top-1 left-1/2 -translate-x-1/2 bg-neon-green text-black px-6 py-1.5 rounded-b-lg text-xs font-black uppercase tracking-wider flex items-center gap-1"
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Star className="w-3 h-3" />
              Recomendado
            </motion.div>

            {/* Animated shine */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(0,255,0,0.05), transparent)',
                backgroundSize: '200% 100%',
              }}
              animate={{
                backgroundPosition: ['200% 0', '-200% 0'],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
            />

            <div className="relative z-10 mt-4">
              <div className="flex items-center gap-2 mb-4">
                <motion.div
                  className="w-10 h-10 rounded-lg bg-neon-green/10 border border-neon-green/30 flex items-center justify-center"
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                >
                  <Crown className="w-5 h-5 text-neon-green" />
                </motion.div>
                <span className="text-neon-green text-sm font-bold uppercase tracking-wider">
                  Verdadeiro Membro
                </span>
              </div>

              <h3 className="font-outfit font-black text-3xl text-white mb-2">
                {membership.name}
              </h3>
              <p className="text-white/70 text-sm mb-6">
                {membership.description}
              </p>

              {/* Toggle */}
              <div className="inline-flex items-center gap-1 p-1 bg-surface-dark border border-white/10 rounded-full mb-6">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    !isAnnual ? 'bg-neon-green text-black' : 'text-white/60'
                  }`}
                  data-testid="toggle-monthly"
                >
                  Mensal
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                    isAnnual ? 'bg-neon-green text-black' : 'text-white/60'
                  }`}
                  data-testid="toggle-annual"
                >
                  Anual
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    isAnnual ? 'bg-black/20 text-black' : 'bg-gold text-black'
                  }`}>
                    -17%
                  </span>
                </button>
              </div>

              <div className="mb-8">
                <div className="flex items-baseline gap-2">
                  <motion.span 
                    key={isAnnual}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-outfit font-black text-5xl gradient-text-green"
                  >
                    {formatPrice(price)}
                  </motion.span>
                  <span className="text-white/60">/{isAnnual ? 'ano' : 'mês'}</span>
                </div>
                {isAnnual && (
                  <p className="text-white/40 text-sm mt-1">
                    equivale a {formatPrice(Math.round(membership.annualPrice / 12))}/mês
                  </p>
                )}
              </div>

              {/* Benefits */}
              <div className="space-y-3 mb-8">
                {membership.benefits.map((benefit, index) => {
                  const Icon = getIcon(benefit.icon);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-6 h-6 flex-shrink-0 rounded-full bg-neon-green/10 border border-neon-green/30 flex items-center justify-center mt-0.5">
                        <Check className="w-3.5 h-3.5 text-neon-green" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{benefit.title}</p>
                        <p className="text-white/60 text-xs">{benefit.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* CTA */}
              <motion.a
                href={membership.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group block w-full py-4 rounded-xl bg-neon-green text-black font-black text-center relative overflow-hidden shadow-[0_0_30px_rgba(0,255,0,0.3)]"
                whileHover={{ scale: 1.02, boxShadow: '0 0 50px rgba(0, 255, 0, 0.5)' }}
                whileTap={{ scale: 0.98 }}
                data-testid="membership-cta"
              >
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                />
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Crown className="w-5 h-5" />
                  Quero Ser Membro
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.a>

              <p className="text-neon-green/80 text-xs text-center mt-4 font-medium">
                Se você quer conexões reais e valor de verdade, é aqui.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Value Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16 max-w-3xl mx-auto"
        >
          <p className="text-white/80 text-lg italic">
            "Se você quer apenas assistir, existem muitos lugares.
            <br />
            <span className="text-neon-green font-semibold not-italic">
              Se você quer se conectar com as pessoas certas e construir valor de forma colaborativa: esse é o seu lugar.
            </span>"
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default MembershipSection;
